/**
 * Date Utility Service
 * 
 * Provides reusable date formatting functions for the entire application.
 * All date formatting should use these utilities to ensure consistency.
 * 
 * @example
 * ```typescript
 * import { DateUtil } from '@core/services/util/date.util';
 * 
 * const formattedDate = DateUtil.formatApprovalDate('2024-01-20 10:30:00');
 * const formattedDate2 = DateUtil.formatDate('2024-01-20');
 * ```
 */
export class DateUtil {
  /**
   * Formats a date string for approval/action dates with time.
   * Format: "Jan 20, 2024, 10:30 AM"
   * 
   * @param dateString - Date string to format (ISO string, date string, etc.)
   * @returns Formatted date string or original string if invalid
   * 
   * @example
   * ```typescript
   * DateUtil.formatApprovalDate('2024-01-20 10:30:00')
   * // Returns: "Jan 20, 2024, 10:30 AM"
   * ```
   */
  static formatApprovalDate(dateString: string): string {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid date

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString; // Return original if parsing fails
    }
  }

  /**
   * Formats a date string for display (date only, no time).
   * Format: "January 20, 2024"
   * 
   * @param dateString - Date string to format (ISO string, date string, etc.)
   * @returns Formatted date string or empty string if invalid
   * 
   * @example
   * ```typescript
   * DateUtil.formatDate('2024-01-20')
   * // Returns: "January 20, 2024"
   * ```
   */
  static formatDate(dateString?: string): string {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return '';
    }
  }

  /**
   * Formats a date string with custom options.
   * 
   * @param dateString - Date string to format
   * @param options - Intl.DateTimeFormatOptions
   * @returns Formatted date string or empty string if invalid
   * 
   * @example
   * ```typescript
   * DateUtil.formatDateCustom('2024-01-20', { year: 'numeric', month: 'short' })
   * // Returns: "Jan 2024"
   * ```
   */
  static formatDateCustom(dateString: string, options: Intl.DateTimeFormatOptions): string {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      return date.toLocaleDateString('en-US', options);
    } catch {
      return '';
    }
  }
}

